import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useCredits } from "@/contexts/CreditsContext";
import { Button } from "@/components/ui/Button";

interface DropdownMenuProps {
  label: string;
  children: React.ReactNode;
  className?: string;
}

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  label,
  children,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div ref={dropdownRef} className={`relative ${className}`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="body-text text-gray-700 hover:text-blazerex-green transition-colors flex items-center gap-1"
      >
        {label}
        <svg
          className={`w-4 h-4 transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-2 z-50">
          {children}
        </div>
      )}
    </div>
  );
};

interface DropdownItemProps {
  to: string;
  children: React.ReactNode;
  onClick?: () => void;
}

const DropdownItem: React.FC<DropdownItemProps> = ({
  to,
  children,
  onClick,
}) => {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="block px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
    >
      {children}
    </Link>
  );
};

export const Header: React.FC = () => {
  const { user, signOut } = useAuth();
  const { balance, loading: creditsLoading } = useCredits();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const checkAdminStatus = async () => {
      if (user) {
        try {
          // Force refresh so new custom claims (e.g. isAdmin) appear without stale JWT
          const idTokenResult = await user.getIdTokenResult(true);
          setIsAdmin(idTokenResult.claims.isAdmin === true);
        } catch (error) {
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [user]);

  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Blazerex Logo" className="h-10 w-auto" />
            <span className="h1 text-2xl" style={{ color: "#76B900" }}>
              Blazerex
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {/* Analysis Dropdown */}
            <DropdownMenu label="Analysis">
              <DropdownItem to="/companies">Companies</DropdownItem>
              <DropdownItem to="/earnings-calls">Earnings Calls</DropdownItem>
              <DropdownItem to="/earnings-calendar">Earnings Calendar</DropdownItem>
              <DropdownItem to="/filings">Financial Reports</DropdownItem>
              <DropdownItem to="/rankings">Rankings</DropdownItem>
              <DropdownItem to="/market-movers">Market Movers</DropdownItem>
            </DropdownMenu>

            {/* Market Data */}
            <Link
              to="/news"
              className="body-text text-gray-700 hover:text-blazerex-green transition-colors"
            >
              News
            </Link>

            <Link
              to="/about"
              className="body-text text-gray-700 hover:text-blazerex-green transition-colors"
            >
              About
            </Link>

            {/* User Features Dropdown */}
            {user && (
              <DropdownMenu label="My Account">
                <DropdownItem to="/dashboard">Dashboard</DropdownItem>
                <DropdownItem to="/copilot">Copilot</DropdownItem>
                <DropdownItem to="/saved">Saved Analyses</DropdownItem>
                {isAdmin && (
                  <DropdownItem to="/admin">Admin Panel</DropdownItem>
                )}
              </DropdownMenu>
            )}

            {/* Pricing */}
            <Link
              to="/pricing"
              className="body-text text-gray-700 hover:text-blazerex-green transition-colors"
            >
              Pricing
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 text-gray-700 hover:text-blazerex-green"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {mobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>

          {/* Auth Buttons */}
          <div className="flex items-center gap-4">
            {user ? (
              <>
                {!creditsLoading && (
                  <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-blazerex-green bg-opacity-10 rounded-lg">
                    <span className="body-text text-sm text-gray-600">
                      Credits:
                    </span>
                    <span className="body-text text-sm font-header-bold text-blazerex-green">
                      {balance}
                    </span>
                  </div>
                )}
                <div className="hidden md:block body-text text-sm text-gray-600">
                  {user.displayName || user.email?.split("@")[0]}
                </div>
                <Button variant="outline" size="sm" onClick={handleSignOut}>
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate("/signup")}
                >
                  Get Started
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col gap-1">
              <div className="px-4 py-2 body-text text-xs text-gray-500 uppercase tracking-wider">
                Analysis
              </div>
              <Link
                to="/companies"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Companies
              </Link>
              <Link
                to="/earnings-calls"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Earnings Calls
              </Link>
              <Link
                to="/earnings-calendar"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Earnings Calendar
              </Link>
              <Link
                to="/filings"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Financial Reports
              </Link>
              <Link
                to="/rankings"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Rankings
              </Link>
              <Link
                to="/market-movers"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Market Movers
              </Link>

              <div className="px-4 py-2 body-text text-xs text-gray-500 uppercase tracking-wider mt-2">
                Market Data
              </div>
              <Link
                to="/news"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                News
              </Link>
              <Link
                to="/about"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>

              {user && (
                <>
                  <div className="px-4 py-2 body-text text-xs text-gray-500 uppercase tracking-wider mt-2">
                    My Account
                  </div>
                  <Link
                    to="/dashboard"
                    className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/copilot"
                    className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Copilot
                  </Link>
                  <Link
                    to="/saved"
                    className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Saved Analyses
                  </Link>
                  <Link
                    to="/subscription"
                    className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Subscription
                  </Link>
                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Panel
                    </Link>
                  )}
                </>
              )}

              <div className="px-4 py-2 body-text text-xs text-gray-500 uppercase tracking-wider mt-2">
                Account
              </div>
              <Link
                to="/pricing"
                className="px-4 py-2 body-text text-gray-700 hover:bg-gray-50 hover:text-blazerex-green transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Pricing
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
